import type { LDContext } from 'launchdarkly-react-client-sdk';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Logger } from '~/lib/logger';
import { useAuth } from '~/providers/AuthProvider';
import { convertToLDIdentityContext } from '~/utils/convertToLDIdentityContext';

const logger = new Logger('useUserFeatureFlagsSync');

/**
 * Subscribes to LaunchDarkly's identity context changes
 *
 * @returns LDContext | null
 */
export function useLDIdentityContext() {
  const ldClient = useLDClient();
  const previousContextRef = useRef<LDContext | null>(null);

  const getSnapshot = () => {
    if (!ldClient) {
      return null;
    }

    const currentContext = ldClient.getContext();

    // Only return a new reference if the context has actually changed
    if (JSON.stringify(previousContextRef.current) !== JSON.stringify(currentContext)) {
      previousContextRef.current = currentContext;
    }

    return previousContextRef.current;
  };

  const subscribe = useCallback(
    (callback: () => void) => {
      if (!ldClient) {
        return () => {};
      }
      const listener = () => callback();
      ldClient.on('change:context', listener);
      return () => ldClient.off('change:context', listener);
    },
    [ldClient],
  );

  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * Synchronizes LaunchDarkly user context with authentication state.
 *
 * @returns
 */
export const useUserFeatureFlagsSync = () => {
  const ldClient = useLDClient();
  const ldIdentityContext = useLDIdentityContext();
  const { token } = useAuth();

  const userContext = convertToLDIdentityContext(token);
  const needsLDIdentificationChanged = userContext.key !== ldIdentityContext?.key;
  const [loading, setLoading] = useState(needsLDIdentificationChanged);

  useEffect(() => {
    if (!ldClient) {
      setLoading(false);
      return;
    }

    if (needsLDIdentificationChanged) {
      setLoading(true);
      (async () => {
        try {
          await ldClient.waitUntilReady();
          await ldClient.identify(userContext);
          logger.info('LD client changed identity success', {
            from: ldIdentityContext?.key,
            to: userContext.key,
          });
        } catch (error) {
          logger.error('Failed to update LD identity', error);
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setLoading(false);
    }
  }, [ldClient, token, needsLDIdentificationChanged, ldIdentityContext?.key, userContext]);

  return {
    loading: loading || needsLDIdentificationChanged,
  };
};
